import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { getUserRoles } from '@/features/roles/api';
import { authStore } from '@/stores/auth';
import type { Role } from '@customerio/hydra-permissions/roles';
import { useStore } from '@nanostores/react';
import { useQuery } from '@tanstack/react-query';

type UserRole = {
  workspace_id: number;
  workspace_name: string;
  role_id: number;
  role_name: Role;
};

export function useUserRoleForCurrentWorkspace(): { name: Role; redacted: boolean } | null {
  const auth = useStore(authStore);
  const workspaceId = getWorkspaceIdFromCurrentUrl();

  const userRolesQuery = useQuery<Record<string, never>, unknown, { user_roles: UserRole[] }>({
    queryKey: ['userRoles', auth.account?.id ?? 0, auth.user?.id ?? 0],
    queryFn: async () => {
      try {
        if (auth.account == null || auth.user == null) {
          return null;
        }

        return await getUserRoles(auth.account.id, auth.user.id);
      } catch {
        return null;
      }
    },
    keepPreviousData: true,
  });

  if (userRolesQuery.data == null) {
    return null;
  }

  const userWorkspaceRole = userRolesQuery.data.user_roles?.find((role) => role.workspace_id === workspaceId);

  if (userWorkspaceRole == null) {
    return null;
  }

  return {
    name: userWorkspaceRole.role_name,
    redacted: userWorkspaceRole.role_name.includes('-redact-on'),
  };
}
